import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../engine/api/context';
import { onStepUp, type StepUpRequest } from '../engine/api/stepUp';
import { Button } from '../engine/design/primitives/Button';
import { Field, Input } from '../engine/design/primitives/Input';
import { Banner, Skeleton } from '../engine/design/primitives/Misc';
import { Dialog } from '../engine/design/primitives/Overlay';
import { useToast } from '../engine/design/primitives/Toast';
import { federatedSession } from '../lib/federatedSession';
import { gotrueTotp } from '../lib/supabase';
import { isAuthConfigured } from '../lib/runtimeConfig';

// The second factor (docs/identity.md → Second factor). Opens when the API answers 403
// `step_up_required` (role and permission changes, API keys, sign-in and connection settings, app
// consent need a code verified in the last 15 minutes), or from the account menu to set up or
// remove the authenticator app. A Halo sign-in uses NovaPSA's own TOTP (/v1/me/mfa); a password
// sign-in uses GoTrue's (supabase-js auth.mfa). Either way the person scans once, then types the
// 6-digit code; the operation is then retried by the person.

type Provider = 'novapsa' | 'gotrue';
type Phase =
  | { step: 'loading' }
  | { step: 'enroll'; secret: string; uri: string; qrCode: string | null; factorId: string | null }
  | { step: 'verify'; factorId: string | null }
  | { step: 'error'; message: string };

const providerOf = (): Provider => (federatedSession.get() || federatedSession.wasActive() || !isAuthConfigured ? 'novapsa' : 'gotrue');
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');
/** A base32 secret in groups of four, easier to type. */
const grouped = (s: string) => s.replace(/(.{4})/g, '$1 ').trim();

/** `enforced`: the organisation requires two-factor and this session has none (from GET /v1/me). */
export function StepUpHost({ enforced = false }: { enforced?: boolean }) {
  const api = useApi();
  const toast = useToast();
  const [req, setReq] = useState<StepUpRequest | null>(null);
  const [phase, setPhase] = useState<Phase>({ step: 'loading' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(false);
  const provider = providerOf();
  const qc = useQueryClient();
  // Enforced: the dialog opens and cannot be dismissed until a code is verified (the API refuses
  // everything else meanwhile).

  useEffect(() => onStepUp((r) => setReq((cur) => cur ?? r)), []);
  useEffect(() => {
    if (enforced) setReq((cur) => cur ?? { mode: 'verify', reason: 'mfa_required' });
  }, [enforced]);

  useEffect(() => {
    if (!req) return;
    let live = true;
    setCode('');
    setError(null);
    setPhase({ step: 'loading' });
    const load = async (): Promise<Phase> => {
      if (provider === 'novapsa') {
        const st = await api.request<{ totp: { enrolled: boolean } | null }>(`${api.prefix}/me/mfa`);
        setEnrolled(Boolean(st.totp?.enrolled));
        if (st.totp?.enrolled) return { step: 'verify', factorId: null };
        if (req.mode === 'manage') return { step: 'verify', factorId: null };
        const e = await api.request<{ secret: string; otpauth_uri: string }>(`${api.prefix}/me/mfa/totp`, { method: 'POST' });
        return { step: 'enroll', secret: e.secret, uri: e.otpauth_uri, qrCode: null, factorId: null };
      }
      const verified = (await gotrueTotp.factors()).find((f) => f.status === 'verified');
      setEnrolled(Boolean(verified));
      if (verified) return { step: 'verify', factorId: verified.id };
      if (req.mode === 'manage') return { step: 'verify', factorId: null };
      const e = await gotrueTotp.enroll();
      return { step: 'enroll', secret: e.secret, uri: e.uri, qrCode: e.qrCode, factorId: e.id };
    };
    load()
      .then((p) => live && setPhase(p))
      .catch((e: unknown) => live && setPhase({ step: 'error', message: errText(e) }));
    return () => {
      live = false;
    };
  }, [req, api, provider]);

  const close = () => {
    if (!enforced) setReq(null);
  };

  const startEnrol = async () => {
    setBusy(true);
    try {
      if (provider === 'novapsa') {
        const e = await api.request<{ secret: string; otpauth_uri: string }>(`${api.prefix}/me/mfa/totp`, { method: 'POST' });
        setPhase({ step: 'enroll', secret: e.secret, uri: e.otpauth_uri, qrCode: null, factorId: null });
      } else {
        const e = await gotrueTotp.enroll();
        setPhase({ step: 'enroll', secret: e.secret, uri: e.uri, qrCode: e.qrCode, factorId: e.id });
      }
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!/^\d{6}$/.test(code.trim()) || phase.step === 'loading' || phase.step === 'error') return;
    setBusy(true);
    setError(null);
    try {
      const factorId = phase.step === 'enroll' || phase.step === 'verify' ? phase.factorId : null;
      if (provider === 'novapsa') {
        const t = await api.request<{ access_token: string; expires_in: number }>(`${api.prefix}/me/mfa/step-up`, { method: 'POST', body: { code: code.trim() } });
        federatedSession.adopt(t);
      } else {
        if (!factorId) throw new Error('Set up your authenticator app first');
        await gotrueTotp.verify(factorId, code.trim());
      }
      toast({ kind: 'success', title: phase.step === 'enroll' ? 'Authenticator app set up' : 'Confirmed', detail: req?.mode === 'verify' && !enforced ? 'Try that again — it will go through now.' : undefined });
      setReq(null);
      // The new token is aal2: reload what the API refused (and /v1/me, which lifts the requirement).
      void qc.invalidateQueries();
    } catch (e) {
      setError(errText(e));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      if (provider === 'novapsa') {
        await api.request(`${api.prefix}/me/mfa/totp`, { method: 'DELETE' });
        toast({ kind: 'success', title: 'Authenticator app removed', detail: 'Every session was signed out — sign in again.' });
        federatedSession.forget();
      } else {
        const f = (await gotrueTotp.factors()).find((x) => x.status === 'verified');
        if (f) await gotrueTotp.unenroll(f.id);
        toast({ kind: 'success', title: 'Authenticator app removed' });
      }
      close();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!req) return null;
  const managing = req.mode === 'manage';
  const title = managing ? 'Two-factor authentication' : phase.step === 'enroll' ? 'Set up your authenticator app' : 'Confirm it is you';
  const description = managing
    ? 'A code from an authenticator app protects role changes, API keys, sign-in settings and app connections.'
    : enforced
      ? 'Your organisation requires two-factor authentication. Enter the code from your authenticator app to continue.'
      : req.reason === 'stale'
      ? 'This needs a code from your authenticator app from the last 15 minutes.'
      : 'This needs a second factor. Enter the code from your authenticator app.';
  const codeForm = (label: string) => (
    <form onSubmit={submit} className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      <Field label={label} error={error ?? undefined}>
        <Input
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </Field>
      <div className="hf-row" style={{ justifyContent: 'flex-end', gap: 'var(--s-2)' }}>
        {!enforced && (
          <Button variant="ghost" onClick={close} type="button">
            Cancel
          </Button>
        )}
        <Button variant="primary" type="submit" disabled={busy || code.length !== 6}>
          {phase.step === 'enroll' ? 'Turn on' : 'Confirm'}
        </Button>
      </div>
    </form>
  );

  return (
    <Dialog open onClose={close} title={title} description={description} locked={enforced}>
      {phase.step === 'loading' ? (
        <Skeleton lines={4} />
      ) : phase.step === 'error' ? (
        <Banner tone="error">{phase.message}</Banner>
      ) : phase.step === 'enroll' ? (
        <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
          <p>Scan this with an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password…), or enter the key by hand.</p>
          {phase.qrCode && <img src={phase.qrCode} alt="QR code for your authenticator app" width={180} height={180} />}
          <div>
            <span className="hf-muted">Key</span>
            <div className="hf-num hf-mono" aria-label="Setup key">{grouped(phase.secret)}</div>
          </div>
          <a href={phase.uri} className="hf-muted">Open in an authenticator app on this device</a>
          {codeForm('Then enter the 6-digit code it shows')}
        </div>
      ) : managing && !enrolled ? (
        <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
          <p>No authenticator app is set up yet.</p>
          {error && <Banner tone="error">{error}</Banner>}
          <div className="hf-row" style={{ justifyContent: 'flex-end' }}>
            <Button variant="primary" onClick={startEnrol} disabled={busy}>
              Set up an authenticator app
            </Button>
          </div>
        </div>
      ) : managing ? (
        <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
          <p>An authenticator app is set up.</p>
          {error && <Banner tone="error">{error}</Banner>}
          <div className="hf-row" style={{ justifyContent: 'flex-end', gap: 'var(--s-2)' }}>
            <Button variant="ghost" onClick={close}>
              Close
            </Button>
            <Button variant="danger" onClick={remove} disabled={busy}>
              Remove it
            </Button>
          </div>
        </div>
      ) : (
        codeForm('6-digit code')
      )}
    </Dialog>
  );
}
